import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createRepo, useProjects, useRepos, type Repo } from '../api/client';
import { FORMATS, isExperimentalFormat } from '../lib/formats';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { MemberSelector } from '../components/MemberSelector';
import { SlugInput, slugIsValid } from '../components/SlugInput';

const KINDS: Repo['kind'][] = ['local', 'remote', 'virtual'];

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function kindTone(kind: Repo['kind']): 'accent' | 'nebula' | 'neutral' {
  if (kind === 'remote') return 'nebula';
  if (kind === 'virtual') return 'accent';
  return 'neutral';
}

function ReposTableBody({
  repos,
  filtered,
  isLoading,
  onRowClick,
}: {
  repos: Repo[] | undefined;
  filtered: Repo[];
  isLoading: boolean;
  onRowClick: (id: string) => void;
}) {
  if (isLoading && !repos) {
    return (
      <>
        {Array.from({ length: 6 }).map((_, i) => (
          <tr key={`sk-${i}`} className="border-b border-border/60 last:border-0">
            <td className="px-5 py-3"><Skeleton width="7rem" /></td>
            <td className="px-5 py-3"><Skeleton width="10rem" /></td>
            <td className="px-5 py-3"><Skeleton width="4rem" height="1.25rem" rounded="9999px" /></td>
            <td className="px-5 py-3"><Skeleton width="5rem" /></td>
            <td className="px-5 py-3"><Skeleton width="60%" /></td>
          </tr>
        ))}
      </>
    );
  }

  if (filtered.length) {
    return (
      <>
        {filtered.map((r) => (
          <tr
            key={r.id}
            className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 cursor-pointer"
            onClick={() => onRowClick(r.id)}
          >
            <td className="px-5 py-3 font-mono text-accent">{r.slug}</td>
            <td className="px-5 py-3 text-fg">{r.name}</td>
            <td className="px-5 py-3">
              <Badge tone={kindTone(r.kind)}>{r.kind}</Badge>
            </td>
            <td className="px-5 py-3"><span className="orb-code">{r.format}</span></td>
            <td className="px-5 py-3 text-fg-muted break-all">{r.upstreamUrl}</td>
          </tr>
        ))}
      </>
    );
  }

  return (
    <tr>
      <td colSpan={5} className="px-5 py-8 text-center text-fg-muted text-sm">
        {repos && repos.length > 0 ? 'No repositories match the filters.' : 'No repositories yet.'}
      </td>
    </tr>
  );
}

export function Repos() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: repos, mutate, isLoading } = useRepos();
  const { data: projects } = useProjects();

  // v189 — pre-select format from `?format=<id>` query param so the
  // dashboard's format-tile click lands here with the right adapter
  // already chosen. `?new=1` additionally scrolls the create-form
  // into view.
  const initialFormat = (() => {
    const fromQuery = searchParams.get('format');
    if (fromQuery && FORMATS.some((f) => f.id === fromQuery)) {
      return fromQuery as Repo['format'];
    }
    return 'maven' as Repo['format'];
  })();

  const [form, setForm] = useState<Omit<Repo, 'id'>>({
    projectId: '', slug: '', name: '', kind: 'local', format: initialFormat, upstreamUrl: '', members: [],
  });
  const [busy, setBusy] = useState(false);

  // Client-side filters over the fetched repo list.
  const [fKind, setFKind] = useState<'all' | Repo['kind']>('all');
  const [fFormat, setFFormat] = useState<'all' | Repo['format']>('all');
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const filtered = (repos ?? []).filter(
    (r) =>
      (fKind === 'all' || r.kind === fKind) &&
      (fFormat === 'all' || r.format === fFormat) &&
      (needle === '' || r.slug.toLowerCase().includes(needle) || r.name.toLowerCase().includes(needle)),
  );
  const totalSuffix = repos && filtered.length !== repos.length ? ` of ${repos.length}` : '';
  const formRef = useRef<HTMLFormElement | null>(null);

  // If the user arrived via "/repos?format=...&new=1" (dashboard click),
  // scroll the create-form into view + clear the query so a refresh
  // doesn't re-trigger the scroll.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      next.delete('format');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Strip members for non-virtual kinds so the backend doesn't have
      // to reject the body for non-virtual + non-empty members.
      const body: Omit<Repo, 'id'> =
        form.kind === 'virtual'
          ? form
          : { ...form, members: undefined };
      await createRepo(body);
      setForm({ ...form, slug: '', name: '', upstreamUrl: '', members: [] });
      await mutate();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">Workspace</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Repositories</h1>
        <p className="text-fg-muted text-sm mt-2">
          One repo per (project, format). Kind controls upstream/proxy semantics:{' '}
          <span className="ml-1"><Badge tone="neutral">local</Badge></span>
          <span className="ml-1"><Badge tone="nebula">remote</Badge></span>
          <span className="ml-1"><Badge tone="accent">virtual</Badge></span>
        </p>
      </div>

      <Card>
        <CardHeader title="Create repository" />
        <CardBody>
          <form ref={formRef} onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              required
              className={selectClass}
            >
              <option value="">— project —</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <SlugInput
              value={form.slug}
              onChange={(slug) => setForm({ ...form, slug })}
              required
            />
            <Input
              placeholder="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as Repo['kind'] })}
              className={selectClass}
            >
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <select
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value as Repo['format'] })}
              className={selectClass}
            >
              {FORMATS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}{isExperimentalFormat(f) ? ' (experimental)' : ''}
                </option>
              ))}
            </select>
            <Input
              placeholder="upstream URL (remote only)"
              value={form.upstreamUrl}
              onChange={(e) => setForm({ ...form, upstreamUrl: e.target.value })}
            />
            {form.kind === 'virtual' && (
              <div className="md:col-span-3 space-y-2">
                <p className="orb-section-title text-fg-muted">
                  Virtual repo members
                </p>
                <p className="text-xs text-fg-muted">
                  A virtual repo unifies several locals/remotes of the same format under a
                  single client URL. Order = resolution priority: a pull tries member 1
                  first, then 2, etc.
                </p>
                <MemberSelector
                  projectId={form.projectId}
                  format={form.format}
                  value={form.members ?? []}
                  onChange={(members) => setForm({ ...form, members })}
                />
              </div>
            )}
            <Button
              variant="primary"
              type="submit"
              disabled={busy || !slugIsValid(form.slug)}
              className="md:col-span-3"
            >
              <Plus className="h-4 w-4" />
              Create repository
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`All repositories (${filtered.length}${totalSuffix})`}
        />
        <div className="px-5 pb-3 flex flex-wrap items-center gap-2">
          <select value={fKind} onChange={(e) => setFKind(e.target.value as 'all' | Repo['kind'])} className={selectClass} aria-label="Filter by kind">
            <option value="all">All kinds</option>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select value={fFormat} onChange={(e) => setFFormat(e.target.value as 'all' | Repo['format'])} className={selectClass} aria-label="Filter by format">
            <option value="all">All formats</option>
            {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search slug / name…"
            className={`${selectClass} flex-1 min-w-[12rem]`}
          />
          {(fKind !== 'all' || fFormat !== 'all' || q !== '') && (
            <button
              type="button"
              onClick={() => { setFKind('all'); setFFormat('all'); setQ(''); }}
              className="h-10 px-3 rounded-md border border-border text-fg-muted hover:text-fg hover:bg-bg-hover/40 text-sm"
            >
              Clear
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Slug</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Kind</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Format</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Upstream</th>
              </tr>
            </thead>
            <tbody>
              <ReposTableBody
                repos={repos}
                filtered={filtered}
                isLoading={isLoading}
                onRowClick={(id) => navigate(`/repos/${id}`)}
              />
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
